import { useState, useEffect, useRef } from 'react';

function VisualPlacementEditor({ 
  templateImageUrl, 
  placements = [], 
  onPlacementsChange,
  orientation = 'horizontal', // 'horizontal' or 'vertical'
  designWidth = 240,
  designHeight = 240
}) {
  const canvasRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [, setImageDimensions] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);
  const [selectedPlacementIndex, setSelectedPlacementIndex] = useState(null);
  const [editingPlacement, setEditingPlacement] = useState(null);

  // Helper to ensure placement horizontal and vertical are independent
  const normalizePlacement = (placement) => {
    if (!placement) return null;
    
    return {
      ...placement,
      horizontal: placement.horizontal ? { 
        x_offset: placement.horizontal.x_offset ?? 0,
        y_offset: placement.horizontal.y_offset ?? 0,
        design_width: placement.horizontal.design_width ?? placement.design_width ?? 240,
        design_height: placement.horizontal.design_height ?? placement.design_height ?? 240,
        rotation: placement.horizontal.rotation ?? placement.rotation ?? 0
      } : {
        x_offset: placement.x_offset_horizontal ?? 0,
        y_offset: placement.y_offset_horizontal ?? 0,
        design_width: placement.design_width ?? 240,
        design_height: placement.design_height ?? 240,
        rotation: placement.rotation ?? 0
      },
      vertical: placement.vertical ? {
        x_offset: placement.vertical.x_offset ?? 0,
        y_offset: placement.vertical.y_offset ?? 0,
        design_width: placement.vertical.design_width ?? placement.design_width ?? 240,
        design_height: placement.vertical.design_height ?? placement.design_height ?? 240,
        rotation: placement.vertical.rotation ?? placement.rotation ?? 0
      } : {
        x_offset: placement.x_offset_vertical ?? 0,
        y_offset: placement.y_offset_vertical ?? 0,
        design_width: placement.design_width ?? 240,
        design_height: placement.design_height ?? 240,
        rotation: placement.rotation ?? 0
      }
    };
  };

  // Load template image
  useEffect(() => {
    if (!templateImageUrl) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImageDimensions({ width: img.width, height: img.height });
      setImageLoaded(true);
      // Calculate scale to fit canvas (max 800px width)
      const maxWidth = 800;
      const calculatedScale = Math.min(1, maxWidth / img.width);
      setScale(calculatedScale);
    };
    img.onerror = () => {
      console.error('Failed to load template image:', templateImageUrl);
      setImageLoaded(false);
    };
    img.src = templateImageUrl;
  }, [templateImageUrl]);

  // Draw canvas
  useEffect(() => {
    if (!imageLoaded || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const displayWidth = img.width * scale;
      const displayHeight = img.height * scale;
      
      canvas.width = displayWidth;
      canvas.height = displayHeight;
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw template image
      ctx.drawImage(img, 0, 0, displayWidth, displayHeight);
      
      // Draw placements
      // Note: This preview matches Sharp's behavior:
      // - Sharp rotates images around their center
      // - Sharp composites at (x, y) which is the top-left corner of the bounding box
      // - The preview shows the rotated bounding box with center at (x + width/2, y + height/2)
      placements.forEach((placement, index) => {
        const isSelected = index === selectedPlacementIndex;
        const placementData = orientation === 'horizontal' 
          ? placement.horizontal || placement 
          : placement.vertical || placement;
        
        // x, y are top-left coordinates (matching server's placement.x, placement.y)
        const x = (placementData.x_offset || placementData.x || 0) * scale;
        const y = (placementData.y_offset || placementData.y || 0) * scale;
        const width = (placementData.design_width ?? placement.design_width ?? designWidth) * scale;
        const height = (placementData.design_height ?? placement.design_height ?? designHeight) * scale;
        const rotation = placementData.rotation ?? placement.rotation ?? 0;
        
        // Save context state
        ctx.save();
        
        // Calculate center point of the rectangle (matching Sharp's rotation center)
        const centerX = x + width / 2;
        const centerY = y + height / 2;
        
        // Translate to center and rotate (matching Sharp's rotate behavior)
        // Sharp rotates clockwise for positive angles, same as canvas
        ctx.translate(centerX, centerY);
        ctx.rotate((rotation * Math.PI) / 180); // Convert degrees to radians
        
        // Draw placement rectangle (centered at origin after translation)
        // This represents the canvas of the rotated image (Sharp maintains original dimensions)
        // The visual content inside rotates, but the canvas size stays width x height
        // When Sharp composites, it places the top-left corner of this canvas at (x, y)
        const strokeColor = isSelected ? '#3b82f6' : '#10b981';
        const baseLineWidth = isSelected ? 3 : 2;
        
        // Draw the rectangle with dashed lines (all sides)
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = baseLineWidth;
        ctx.setLineDash(isSelected ? [] : [5, 5]);
        ctx.strokeRect(-width / 2, -height / 2, width, height);
        
        // Draw the top edge as a solid, thicker line to indicate orientation
        // Top edge: from (-width/2, -height/2) to (width/2, -height/2)
        ctx.setLineDash([]); // Solid line
        ctx.lineWidth = baseLineWidth + 2; // Thicker than the dashed line
        ctx.strokeStyle = strokeColor;
        ctx.beginPath();
        ctx.moveTo(-width / 2, -height / 2);
        ctx.lineTo(width / 2, -height / 2);
        ctx.stroke();
        
        // Restore context before drawing non-rotated elements
        ctx.restore();
        
        // Draw placement number (not rotated, positioned above the rectangle)
        ctx.save();
        ctx.fillStyle = isSelected ? '#3b82f6' : '#10b981';
        // Calculate position for number badge (above top-left corner, accounting for rotation)
        const rotationRad = (rotation * Math.PI) / 180;
        const cos = Math.cos(rotationRad);
        const sin = Math.sin(rotationRad);
        // Top-left corner relative to center
        const topLeftX = -width / 2;
        const topLeftY = -height / 2;
        // Rotate the top-left corner
        const rotatedTopLeftX = topLeftX * cos - topLeftY * sin;
        const rotatedTopLeftY = topLeftX * sin + topLeftY * cos;
        // Translate back to canvas coordinates
        const badgeX = centerX + rotatedTopLeftX;
        const badgeY = centerY + rotatedTopLeftY - 20;
        
        ctx.fillRect(badgeX - 12.5, badgeY, 25, 20);
        ctx.fillStyle = 'white';
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(index + 1, badgeX, badgeY + 14);
        ctx.restore();
        
        // Draw rotation indicator if rotated
        if (rotation && rotation !== 0) {
          ctx.save();
          // Calculate position for rotation indicator (top-right corner, accounting for rotation)
          const topRightX = width / 2;
          const topRightY = -height / 2;
          // Rotate the top-right corner
          const rotatedTopRightX = topRightX * cos - topRightY * sin;
          const rotatedTopRightY = topRightX * sin + topRightY * cos;
          // Translate back to canvas coordinates
          const indicatorX = centerX + rotatedTopRightX - 20;
          const indicatorY = centerY + rotatedTopRightY;
          
          ctx.fillStyle = '#f59e0b';
          ctx.fillRect(indicatorX, indicatorY, 20, 20);
          ctx.fillStyle = 'white';
          ctx.font = 'bold 10px Arial';
          ctx.textAlign = 'center';
          ctx.fillText(`${rotation}°`, indicatorX + 10, indicatorY + 14);
          ctx.restore();
        }
      });
    };
    
    img.src = templateImageUrl;
  }, [templateImageUrl, imageLoaded, scale, placements, selectedPlacementIndex, orientation, designWidth, designHeight]);

  const handleCanvasClick = (e) => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;
    
    // Check if clicking on existing placement
    let clickedPlacement = null;
    placements.forEach((placement, index) => {
      const placementData = orientation === 'horizontal' 
        ? placement.horizontal || placement 
        : placement.vertical || placement;
      
      const px = placementData.x_offset || placementData.x || 0;
      const py = placementData.y_offset || placementData.y || 0;
      const width = placementData.design_width ?? placement.design_width ?? designWidth;
      const height = placementData.design_height ?? placement.design_height ?? designHeight;
      const rotation = placementData.rotation ?? placement.rotation ?? 0;
      
      // Check if point is inside rotated rectangle
      const centerX = px + width / 2;
      const centerY = py + height / 2;
      
      // Transform click point to rotated coordinate system
      const rotationRad = (rotation * Math.PI) / 180;
      const cos = Math.cos(-rotationRad); // Negative to reverse the rotation
      const sin = Math.sin(-rotationRad);
      
      // Translate point relative to center
      const dx = x - centerX;
      const dy = y - centerY;
      
      // Rotate point back
      const rotatedX = dx * cos - dy * sin;
      const rotatedY = dx * sin + dy * cos;
      
      // Check if point is inside axis-aligned rectangle
      if (rotatedX >= -width / 2 && rotatedX <= width / 2 && 
          rotatedY >= -height / 2 && rotatedY <= height / 2) {
        clickedPlacement = index;
      }
    });
    
    if (clickedPlacement !== null) {
      setSelectedPlacementIndex(clickedPlacement);
      // Normalize placement to ensure horizontal and vertical are independent
      setEditingPlacement(normalizePlacement(placements[clickedPlacement]));
    } else {
      // Add new placement at clicked position
      const newPlacement = {
        vertical: {
          x_offset: Math.round(x),
          y_offset: Math.round(y),
          design_width: designWidth,
          design_height: designHeight,
          rotation: 0
        },
        horizontal: {
          x_offset: Math.round(x),
          y_offset: Math.round(y),
          design_width: designWidth,
          design_height: designHeight,
          rotation: 0
        }
      };
      
      const updatedPlacements = [...placements, newPlacement];
      onPlacementsChange(updatedPlacements);
      setSelectedPlacementIndex(updatedPlacements.length - 1);
      setEditingPlacement(newPlacement);
    }
  };

  const handleUpdatePlacement = (index, updatedPlacement) => {
    const updatedPlacements = [...placements];
    // Ensure the updated placement has independent horizontal and vertical
    const normalizedPlacement = normalizePlacement(updatedPlacement);
    // Ensure we're creating a completely new object, not just copying
    updatedPlacements[index] = {
      ...normalizedPlacement,
      horizontal: {
        x_offset: normalizedPlacement.horizontal?.x_offset ?? 0,
        y_offset: normalizedPlacement.horizontal?.y_offset ?? 0,
        design_width: normalizedPlacement.horizontal?.design_width ?? 240,
        design_height: normalizedPlacement.horizontal?.design_height ?? 240,
        rotation: normalizedPlacement.horizontal?.rotation ?? 0
      },
      vertical: {
        x_offset: normalizedPlacement.vertical?.x_offset ?? 0,
        y_offset: normalizedPlacement.vertical?.y_offset ?? 0,
        design_width: normalizedPlacement.vertical?.design_width ?? 240,
        design_height: normalizedPlacement.vertical?.design_height ?? 240,
        rotation: normalizedPlacement.vertical?.rotation ?? 0
      }
    };
    onPlacementsChange(updatedPlacements);
    setEditingPlacement(updatedPlacements[index]);
  };

  const handleDeletePlacement = (index) => {
    if (window.confirm('Delete this placement?')) {
      const updatedPlacements = placements.filter((_, i) => i !== index);
      onPlacementsChange(updatedPlacements);
      setSelectedPlacementIndex(null);
      setEditingPlacement(null);
    }
  };

  if (!templateImageUrl) {
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500">
        No template image selected
      </div>
    );
  }

  if (!imageLoaded) {
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-gray-600">Loading template image...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Canvas on the left */}
        <div className="lg:col-span-2 border-2 border-gray-300 rounded-lg p-4 bg-white">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700">
              Click on the template to add placements ({orientation})
            </p>
            <span className="text-xs text-gray-500">
              Scale: {(scale * 100).toFixed(0)}%
            </span>
          </div>
          <canvas
            ref={canvasRef}
            onClick={handleCanvasClick}
            className="border border-gray-200 rounded cursor-crosshair max-w-full"
            style={{ maxHeight: '600px' }}
          />
        </div>

        {/* Placement Editor on the right */}
        {editingPlacement && selectedPlacementIndex !== null ? (
          <div className="lg:col-span-1 bg-white border border-gray-300 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Placement {selectedPlacementIndex + 1}
              </h3>
              <button
                onClick={() => handleDeletePlacement(selectedPlacementIndex)}
                className="px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
              >
                Delete
              </button>
            </div>
            
            <PlacementForm
              key={`${selectedPlacementIndex}-${orientation}`}
              placement={editingPlacement}
              orientation={orientation}
              onUpdate={(updated) => handleUpdatePlacement(selectedPlacementIndex, updated)}
            />
          </div>
        ) : (
          <div className="lg:col-span-1 bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-center">
            <p className="text-sm text-gray-500 text-center">
              Click on the template or a placement to edit
            </p>
          </div>
        )}
      </div>

      {/* Placements List */}
      {placements.length > 0 && (
        <div className="bg-white border border-gray-300 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Placements ({placements.length})
          </h3>
          <div className="space-y-2">
            {placements.map((placement, index) => {
              const placementData = orientation === 'horizontal' 
                ? placement.horizontal || placement 
                : placement.vertical || placement;
              
              return (
                <div
                  key={index}
                  onClick={() => {
                    setSelectedPlacementIndex(index);
                    // Normalize placement to ensure horizontal and vertical are independent
                    setEditingPlacement(normalizePlacement(placement));
                  }}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedPlacementIndex === index
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-gray-900">Placement {index + 1}</span>
                      <span className="ml-2 text-sm text-gray-600">
                        ({placementData.x_offset || placementData.x || 0}, {placementData.y_offset || placementData.y || 0})
                      </span>
                    </div>
                    <div className="text-sm text-gray-500">
                      <span>{Number(placementData.design_width ?? placement.design_width ?? designWidth)}</span>
                      <span> × </span>
                      <span>{Number(placementData.design_height ?? placement.design_height ?? designHeight)}</span>
                      {((placementData.rotation ?? placement.rotation) != null && Number(placementData.rotation ?? placement.rotation) !== 0) && (
                        <span className="ml-2">• {placementData.rotation ?? placement.rotation}°</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Placement Form Component
function PlacementForm({ placement, orientation, onUpdate }) {
  // Helper to ensure horizontal and vertical are independent objects
  const ensureIndependentPlacement = (placementData) => {
    if (!placementData) {
      return {
        vertical: { 
          x_offset: 0, 
          y_offset: 0,
          design_width: 240,
          design_height: 240,
          rotation: 0
        },
        horizontal: { 
          x_offset: 0, 
          y_offset: 0,
          design_width: 240,
          design_height: 240,
          rotation: 0
        }
      };
    }
    
    // Always create completely new separate objects for horizontal and vertical
    // Include design_width, design_height, and rotation in each orientation
    return {
      vertical: {
        x_offset: placementData.vertical?.x_offset ?? placementData.x_offset_vertical ?? 0,
        y_offset: placementData.vertical?.y_offset ?? placementData.y_offset_vertical ?? 0,
        design_width: placementData.vertical?.design_width ?? placementData.design_width ?? 240,
        design_height: placementData.vertical?.design_height ?? placementData.design_height ?? 240,
        rotation: placementData.vertical?.rotation ?? placementData.rotation ?? 0
      },
      horizontal: {
        x_offset: placementData.horizontal?.x_offset ?? placementData.x_offset_horizontal ?? 0,
        y_offset: placementData.horizontal?.y_offset ?? placementData.y_offset_horizontal ?? 0,
        design_width: placementData.horizontal?.design_width ?? placementData.design_width ?? 240,
        design_height: placementData.horizontal?.design_height ?? placementData.design_height ?? 240,
        rotation: placementData.horizontal?.rotation ?? placementData.rotation ?? 0
      }
    };
  };

  const [formData, setFormData] = useState(() => ensureIndependentPlacement(placement));
  
  // Use a ref to track the last placement we processed to avoid loops
  // Initialize with the normalized placement
  const initialPlacement = ensureIndependentPlacement(placement);
  const lastPlacementRef = useRef(initialPlacement);
  
  // Update formData when placement prop changes (e.g., when switching placements)
  useEffect(() => {
    // Compare the normalized placement content, not the raw prop
    const normalized = ensureIndependentPlacement(placement);
    const normalizedStr = JSON.stringify(normalized);
    const lastStr = JSON.stringify(lastPlacementRef.current);
    
    // Only update if the content actually changed
    if (normalizedStr !== lastStr) {
      setFormData(normalized);
      lastPlacementRef.current = normalized;
    }
  }, [placement]);

  const updateField = (path, value) => {
    setFormData(prev => {
      // Always create completely new objects to ensure horizontal and vertical remain independent
      // Explicitly copy each property to avoid any shared references
      const newData = {
        // Always create completely new separate objects for horizontal and vertical
        horizontal: {
          x_offset: prev.horizontal?.x_offset ?? 0,
          y_offset: prev.horizontal?.y_offset ?? 0,
          design_width: prev.horizontal?.design_width ?? prev.design_width ?? 240,
          design_height: prev.horizontal?.design_height ?? prev.design_height ?? 240,
          rotation: prev.horizontal?.rotation ?? prev.rotation ?? 0
        },
        vertical: {
          x_offset: prev.vertical?.x_offset ?? 0,
          y_offset: prev.vertical?.y_offset ?? 0,
          design_width: prev.vertical?.design_width ?? prev.design_width ?? 240,
          design_height: prev.vertical?.design_height ?? prev.design_height ?? 240,
          rotation: prev.vertical?.rotation ?? prev.rotation ?? 0
        }
      };
      
      // Now update only the field specified in the path
      const keys = path.split('.');
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        // Ensure we're creating new objects at each level
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        } else {
          // Create a new object copy
          current[keys[i]] = { ...current[keys[i]] };
        }
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = parseInt(value) || 0;
      
      // Update the ref to prevent the placement useEffect from resetting our changes
      lastPlacementRef.current = {
        vertical: {
          x_offset: newData.vertical.x_offset,
          y_offset: newData.vertical.y_offset,
          design_width: newData.vertical.design_width,
          design_height: newData.vertical.design_height,
          rotation: newData.vertical.rotation
        },
        horizontal: {
          x_offset: newData.horizontal.x_offset,
          y_offset: newData.horizontal.y_offset,
          design_width: newData.horizontal.design_width,
          design_height: newData.horizontal.design_height,
          rotation: newData.horizontal.rotation
        }
      };
      
      // Call onUpdate with the updated data - ensure we create new objects
      onUpdate({
        vertical: {
          x_offset: newData.vertical.x_offset,
          y_offset: newData.vertical.y_offset,
          design_width: newData.vertical.design_width,
          design_height: newData.vertical.design_height,
          rotation: newData.vertical.rotation
        },
        horizontal: {
          x_offset: newData.horizontal.x_offset,
          y_offset: newData.horizontal.y_offset,
          design_width: newData.horizontal.design_width,
          design_height: newData.horizontal.design_height,
          rotation: newData.horizontal.rotation
        }
      });
      
      return newData;
    });
  };

  // Get the current orientation's data
  const currentOrientationData = orientation === 'horizontal' ? formData.horizontal : formData.vertical;

  return (
    <div className="space-y-4">
      {/* Current Orientation Parameters */}
      <div className="border border-gray-200 rounded-lg p-3">
        <h4 className="font-medium text-gray-700 mb-3 capitalize">{orientation} Position</h4>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">X Offset</label>
            <input
              type="number"
              value={currentOrientationData.x_offset ?? 0}
              onChange={(e) => updateField(`${orientation}.x_offset`, e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Y Offset</label>
            <input
              type="number"
              value={currentOrientationData.y_offset ?? 0}
              onChange={(e) => updateField(`${orientation}.y_offset`, e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Width</label>
            <input
              type="number"
              value={currentOrientationData.design_width ?? formData.design_width ?? 240}
              onChange={(e) => updateField(`${orientation}.design_width`, e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Height</label>
            <input
              type="number"
              value={currentOrientationData.design_height ?? formData.design_height ?? 240}
              onChange={(e) => updateField(`${orientation}.design_height`, e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Rotation (°)</label>
            <input
              type="number"
              value={currentOrientationData.rotation ?? formData.rotation ?? 0}
              onChange={(e) => updateField(`${orientation}.rotation`, e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              step="1"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default VisualPlacementEditor;

